import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Users, MapPin, Building2, Tag, Shapes, CheckSquare, ClipboardCheck, DollarSign, Settings as SettingsIcon, LayoutDashboard, Package, CalendarDays, ClipboardList, Mail } from 'lucide-react';

const Settings = () => {
    const navigate = useNavigate();

    const settingsSections = [
        {
            id: 'users',
            title: 'User Management',
            description: 'Add, edit, and manage users across all roles',
            icon: Users,
            color: 'from-blue-500 to-blue-600',
            route: '/admin/settings/users'
        },
        {
            id: 'locations',
            title: 'Location Master',
            description: 'Manage cities and states for project locations',
            icon: MapPin,
            color: 'from-green-500 to-green-600',
            route: '/admin/settings/locations'
        },
        {
            id: 'entity-checklist',
            title: 'Entity & Checklist Master',
            description: 'Manage installation entities and their checklist items',
            icon: Package,
            color: 'from-purple-500 to-purple-600',
            route: '/admin/settings/entity-checklist'
        },
        {
            id: 'sv1-checklist',
            title: 'SV1 Checklist',
            description: 'Manage Site Visit 1 checklist items',
            icon: CheckSquare,
            color: 'from-green-500 to-emerald-600',
            route: '/admin/settings/sv1-checklist'
        },
        {
            id: 'sv2-checklist',
            title: 'SV2 Checklist',
            description: 'Manage Site Visit 2 checklist items',
            icon: ClipboardCheck,
            color: 'from-amber-500 to-orange-600',
            route: '/admin/settings/sv2-checklist'
        },
        {
            id: 'client-review-checklist',
            title: 'Client Review Checklist',
            description: 'Manage client review checklist items',
            icon: ClipboardList,
            color: 'from-cyan-500 to-teal-600',
            route: '/admin/settings/client-review-checklist'
        },
        {
            id: 'holidays',
            title: 'Holidays',
            description: 'Manage public holidays and company holidays for the year',
            icon: CalendarDays,
            color: 'from-red-500 to-rose-600',
            route: '/admin/settings/holidays'
        },
        {
            id: 'email-templates',
            title: 'Email Templates',
            description: 'Manage notification email templates and their content',
            icon: Mail,
            color: 'from-purple-500 to-pink-600',
            route: '/admin/settings/email-templates'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shrink-0">
                                <SettingsIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Settings</h1>
                                <p className="text-gray-600 text-sm sm:text-base">Manage system configuration and master data</p>
                            </div>
                        </div>
                        <div className="flex items-center flex-wrap gap-1 sm:gap-3">
                            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-gray-600">
                                <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
                            </Button>
                            <span className="text-gray-400">/</span>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')} className="text-gray-600">
                                <SettingsIcon className="w-4 h-4 mr-1" /> Settings
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Settings Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {settingsSections.map((section) => {
                        const Icon = section.icon;
                        return (
                            <Card
                                key={section.id}
                                className="hover:shadow-lg transition-all duration-300 cursor-pointer border-2 hover:border-teal-500"
                                onClick={() => navigate(section.route)}
                            >
                                <CardHeader>
                                    <div className={`w-14 h-14 bg-gradient-to-br ${section.color} rounded-xl flex items-center justify-center mb-4`}>
                                        <Icon className="h-7 w-7 text-white" />
                                    </div>
                                    <CardTitle className="text-xl">{section.title}</CardTitle>
                                    <CardDescription className="text-gray-600">
                                        {section.description}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-teal-600 font-medium">
                                            Manage →
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}


                </div>

                {/* Info Section */}
                <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">📋 Quick Tips</h3>
                    <ul className="text-blue-700 space-y-2">
                        <li>• <strong>User Management:</strong> Add new users, update profiles, or deactivate accounts</li>
                        <li>• <strong>Location Master:</strong> Add cities and states for accurate project location tracking</li>
                        <li>• <strong>Pricing Type Master:</strong> Define pricing categories (Kitchen Wall Units, Kitchen Base Units, Wardrobes)</li>
                    </ul>
                </div>
            </div>
        </div >
    );
};

export default Settings;
